import ajaxRequest from "@/utils/ajaxRequest.js";
import processAjaxResponse from "@/utils/processAjaxResponse.js";
import recaptchaToken from "@/utils/recaptchaToken.js";
import showMessage from "@/utils/showMessage.js";
import noScroll from "@/utils/noScroll.js";

const OTP_REQUEST_URL = "/_ajax/tests/otp/request";
const TEST_FINISH_URL = "/_ajax/tests/finish";

// Подстановка вида «Вы набрали {correct} из {total}» — тексты приходят из CMS,
// поэтому значения вставляются по именам, а не по порядку.
function format(template, values) {
    return Object.entries(values).reduce(
        (result, [key, value]) => result.replaceAll(`{${key}}`, value),
        template || ""
    );
}

/**
 * Прохождение теста: диалог подтверждения почты (имя и адрес → код из письма),
 * вопросы и экран результата.
 *
 * Вопросы приходят готовой разметкой, скрипт держит выбор, прогресс, режим
 * показа и отправку ответов. Правильных вариантов на клиенте нет — считает
 * сервер.
 */
export default function testFlow(element, data) {
    const testId = data.testId;
    const codeLength = data.codeLength || 4;
    const resendDelay = data.resendDelay || 60;
    const strings = element.querySelector("[data-test-strings]").dataset;

    const intro = element.querySelector("[data-test-intro]");
    const start = element.querySelector("[data-test-start]");
    const run = element.querySelector("[data-test-run]");
    const result = element.querySelector("[data-test-result]");

    const gate = element.querySelector("[data-test-gate]");
    const identityForm = element.querySelector('[data-test-step="identity"]');
    const codeForm = element.querySelector('[data-test-step="code"]');
    const codeCells = Array.from(element.querySelectorAll(".test-gate-code-cell"));
    const codeDescription = element.querySelector("[data-test-code-description]");
    const resendButton = element.querySelector("[data-test-resend]");

    const questionsForm = element.querySelector("[data-test-questions]");
    const questionNodes = questionsForm
        ? Array.from(questionsForm.querySelectorAll("[data-test-question]"))
        : [];
    const modeButtons = Array.from(element.querySelectorAll("[data-test-mode]"));
    const prevButton = element.querySelector("[data-test-prev]");
    const materialBack = element.querySelector("[data-test-material-back]");
    const dots = Array.from(element.querySelectorAll("[data-test-dot]"));

    let verified = Boolean(data.verified);
    let mode = data.mode === "single" ? "single" : "all";
    let current = 0;
    let email = "";
    let startedAt = verified ? Date.now() : 0;
    let elapsed = 0;
    let resendTimer = null;
    let resendLocked = false;

    // --- Диалог -------------------------------------------------------------

    let gateOpen = false;

    function openGate() {
        if (gateOpen) {
            return;
        }
        gateOpen = true;
        gate.hidden = false;
        noScroll.hideScroll();
        focusFirstField();
    }

    function closeGate() {
        if (!gateOpen) {
            return;
        }
        gateOpen = false;
        gate.hidden = true;
        noScroll.showScroll();
    }

    function focusFirstField() {
        const field = codeForm.hidden
            ? identityForm.querySelector('[name="email"]')
            : codeCells[0];

        // Диалог только что показали — ждём кадр, иначе фокус не встаёт.
        requestAnimationFrame(() => field && field.focus());
    }

    function showStep(step) {
        identityForm.hidden = step !== "identity";
        codeForm.hidden = step !== "code";
        focusFirstField();
    }

    // Адрес в описании — отдельным узлом, поэтому текст собираем не заменой
    // строки, а по частям.
    function renderCodeDescription() {
        const parts = format(strings.strCodeDescription, { length: codeLength }).split("{email}");
        const emailNode = document.createElement("span");
        emailNode.className = "test-gate-email";
        emailNode.textContent = email;

        codeDescription.textContent = "";
        parts.forEach((part, index) => {
            codeDescription.append(part);
            if (index < parts.length - 1) {
                codeDescription.append(emailNode.cloneNode(true));
            }
        });
    }

    // --- Отправка форм ------------------------------------------------------

    function setLoading(form, loading) {
        form.classList.toggle("loading", loading);
        Array.from(form.elements).forEach((field) => {
            // Кнопку повторной отправки держит отсчёт, снятие блокировки формы
            // не должно её разблокировать раньше времени.
            field.disabled = loading || (field === resendButton && resendLocked);
        });
    }

    function markError(target) {
        const errorTarget = target.closest(".field-error-target") || target;
        errorTarget.classList.add("field-error");
    }

    function clearErrors(form) {
        form.querySelectorAll(".field-error").forEach((node) => node.classList.remove("field-error"));
    }

    async function requestCode(values) {
        clearErrors(identityForm);
        setLoading(identityForm, true);

        const response = await ajaxRequest(OTP_REQUEST_URL, {
            ...values,
            recaptcha_token: await recaptchaToken(),
        });

        setLoading(identityForm, false);

        processAjaxResponse(response, (message, payload) => {
            email = payload.email;
            renderCodeDescription();
            showStep("code");
            startResendCountdown();

            // Почта не настроена (обычно локальная разработка) — сервер отдал
            // код в ответе, чтобы шаг можно было пройти.
            if (payload.debugCode) {
                showMessage(format(strings.strDebugCode, { code: payload.debugCode }));
            }
        }, (message, payload) => {
            showMessage(message);
            (payload?.errorFields || []).forEach((field) => {
                const node = identityForm.querySelector(`[name="${field}"]`);
                if (node) {
                    markError(node);
                }
            });
        });
    }

    identityForm.addEventListener("submit", function (event) {
        event.preventDefault();

        requestCode({
            name: identityForm.elements.name.value.trim(),
            email: identityForm.elements.email.value.trim(),
            sendResult: identityForm.elements.sendResult.checked,
        });
    });

    codeForm.addEventListener("submit", async function (event) {
        event.preventDefault();

        const code = codeCells.map((cell) => cell.value).join("");

        if (code.length < codeLength) {
            markError(codeCells[0]);
            showMessage(strings.strCodeIncomplete);
            return;
        }

        clearErrors(codeForm);
        setLoading(codeForm, true);

        const response = await ajaxRequest(codeForm.action, { code });

        setLoading(codeForm, false);

        processAjaxResponse(response, () => {
            verified = true;
            closeGate();
            beginTest();
        }, (message) => {
            showMessage(message);
            markError(codeCells[0]);
            codeCells.forEach((cell) => (cell.value = ""));
            codeCells[0].focus();
        });
    });

    // --- Повторная отправка кода --------------------------------------------

    function startResendCountdown() {
        let left = resendDelay;

        clearInterval(resendTimer);
        resendLocked = true;
        resendButton.disabled = true;

        const tick = () => {
            if (left <= 0) {
                clearInterval(resendTimer);
                resendLocked = false;
                resendButton.disabled = false;
                resendButton.textContent = strings.strResend;
                return;
            }

            resendButton.textContent = format(strings.strResendWait, { seconds: left });
            left -= 1;
        };

        tick();
        resendTimer = setInterval(tick, 1000);
    }

    resendButton.addEventListener("click", function () {
        requestCode({
            name: identityForm.elements.name.value.trim(),
            email: email || identityForm.elements.email.value.trim(),
            sendResult: identityForm.elements.sendResult.checked,
        });
    });

    element.querySelector("[data-test-change-email]").addEventListener("click", function () {
        clearInterval(resendTimer);
        resendLocked = false;
        codeCells.forEach((cell) => (cell.value = ""));
        showStep("identity");
    });

    // --- Поля кода ----------------------------------------------------------

    codeCells.forEach((cell, index) => {
        cell.addEventListener("input", function () {
            cell.value = cell.value.replace(/\D/g, "").slice(-1);
            clearErrors(codeForm);

            if (cell.value && index < codeCells.length - 1) {
                codeCells[index + 1].focus();
            }

            // Код набран целиком — не заставляем жать кнопку.
            if (codeCells.every((item) => item.value)) {
                codeForm.requestSubmit();
            }
        });

        cell.addEventListener("keydown", function (event) {
            if (event.key === "Backspace" && !cell.value && index > 0) {
                event.preventDefault();
                codeCells[index - 1].value = "";
                codeCells[index - 1].focus();
            }
        });

        // Код из письма обычно вставляют целиком — раскладываем его по ячейкам.
        cell.addEventListener("paste", function (event) {
            const pasted = (event.clipboardData || window.clipboardData).getData("text").replace(/\D/g, "");

            if (!pasted) {
                return;
            }

            event.preventDefault();

            codeCells.forEach((item, itemIndex) => {
                if (itemIndex >= index) {
                    item.value = pasted[itemIndex - index] || "";
                }
            });

            const lastFilled = codeCells.filter((item) => item.value).length - 1;
            codeCells[Math.min(Math.max(lastFilled, 0), codeCells.length - 1)].focus();

            if (codeCells.every((item) => item.value)) {
                codeForm.requestSubmit();
            }
        });
    });

    // --- Вопросы ------------------------------------------------------------

    function isAnswered(node) {
        return Boolean(node.querySelector(".test-option-input:checked"));
    }

    function updateProgress() {
        const answered = questionNodes.filter(isAnswered).length;
        const percent = Math.round((answered / questionNodes.length) * 100);

        element.querySelector("[data-test-progress-label]").textContent = format(strings.strProgress, {
            answered,
            total: questionNodes.length,
        });
        element.querySelector("[data-test-progress-percent]").textContent = `${percent}%`;
        element.querySelector("[data-test-progress-bar]").style.width = `${percent}%`;
    }

    // В режиме «по одному» показываем один вопрос, остальные остаются в
    // разметке — ответы на них никуда не деваются.
    function showQuestion(index) {
        current = Math.min(Math.max(index, 0), questionNodes.length - 1);

        questionNodes.forEach((node, nodeIndex) => {
            node.hidden = mode === "single" && nodeIndex !== current;
        });
        dots.forEach((dot, dotIndex) => dot.classList.toggle("active", dotIndex === current));

        const last = current === questionNodes.length - 1;
        const first = current === 0;

        // На первом вопросе «Назад» некуда — вместо неё показываем ссылку на материал
        prevButton.hidden = Boolean(materialBack) && first;
        prevButton.disabled = first;

        if (materialBack) {
            materialBack.hidden = !first;
        }

        element.querySelector("[data-test-next-label]").textContent = last
            ? strings.strFinish
            : strings.strNext;
    }

    function setMode(next) {
        mode = next === "single" ? "single" : "all";
        element.dataset.mode = mode;

        modeButtons.forEach((button) => {
            button.classList.toggle("active", button.dataset.testMode === mode);
        });
        element.querySelector('[data-test-actions="all"]').hidden = mode !== "all";
        element.querySelector('[data-test-actions="single"]').hidden = mode !== "single";
        element.querySelector("[data-test-dots]").hidden = mode !== "single";

        showQuestion(current);
    }

    function collectAnswers() {
        return questionNodes.reduce((answers, node) => {
            answers[node.dataset.questionId] = Array.from(
                node.querySelectorAll(".test-option-input:checked")
            ).map((input) => input.value);

            return answers;
        }, {});
    }

    async function finishTest() {
        const missing = questionNodes.findIndex((node) => !isAnswered(node));

        if (missing >= 0) {
            showMessage(strings.strUnanswered);
            showQuestion(missing);
            questionNodes[missing].classList.add("unanswered");
            questionNodes[missing].scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }

        // Время прохождения знает только клиент: сервер записывает его в
        // журнал попыток и показывает на экране результата.
        elapsed = Math.max(1, Math.round((Date.now() - startedAt) / 1000));

        const response = await ajaxRequest(TEST_FINISH_URL, {
            test: testId,
            answers: collectAnswers(),
            elapsed,
        });

        processAjaxResponse(response, (message, payload) => showResult(payload), (message) => {
            showMessage(message);

            // Чаще всего причина отказа — слетевшее подтверждение почты
            // (истекла кука), поэтому просим подтвердить заново.
            verified = false;
            openGate();
        });
    }

    // --- Состояния страницы -------------------------------------------------

    function beginTest() {
        startedAt = Date.now();
        start.hidden = true;
        if (intro) {
            intro.hidden = false;
        }

        if (questionsForm) {
            questionsForm.reset();
            questionNodes.forEach((node) => node.classList.remove("unanswered"));
            showQuestion(0);
            updateProgress();
        }

        run.hidden = false;
        result.hidden = true;
    }

    function formatElapsed(seconds) {
        const minutes = Math.floor(seconds / 60);
        const rest = seconds % 60;

        return minutes
            ? format(strings.strTimeMinSec, { minutes, seconds: rest })
            : format(strings.strTimeSec, { seconds: rest });
    }

    function showResult(payload) {
        const passed = Boolean(payload.passed);

        result.classList.toggle("passed", passed);
        result.classList.toggle("failed", !passed);

        element.querySelector("[data-test-percent]").textContent = `${payload.percent}%`;
        element.querySelector("[data-test-result-title]").textContent = passed
            ? strings.strPassedTitle
            : strings.strFailedTitle;

        const summary = element.querySelector("[data-test-result-summary]");
        summary.textContent = format(strings.strSummary, {
            correct: payload.correct,
            total: payload.total,
            threshold: payload.threshold,
        });

        if (!passed) {
            summary.append(document.createElement("br"), strings.strFailedHint);
        }

        element.querySelector("[data-test-correct]").textContent = payload.correct;
        element.querySelector("[data-test-wrong]").textContent = payload.wrong;
        element.querySelector("[data-test-elapsed]").textContent = formatElapsed(elapsed);

        const sent = element.querySelector("[data-test-sent]");
        sent.hidden = !payload.resultSent;
        sent.textContent = format(strings.strSent, { email: payload.email || "" });

        // Совета вернуться к материалу нет, если тест ни к чему не привязан.
        const material = element.querySelector("[data-test-material]");
        if (material) {
            material.hidden = passed;
        }

        if (intro) {
            intro.hidden = true;
        }
        run.hidden = true;
        result.hidden = false;
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    if (questionsForm) {
        questionsForm.addEventListener("submit", (event) => event.preventDefault());

        questionsForm.addEventListener("change", function (event) {
            const question = event.target.closest("[data-test-question]");

            if (question) {
                question.classList.remove("unanswered");
            }

            updateProgress();
        });

        modeButtons.forEach((button) => {
            button.addEventListener("click", () => setMode(button.dataset.testMode));
        });

        dots.forEach((dot, index) => dot.addEventListener("click", () => showQuestion(index)));

        element.querySelector("[data-test-prev]").addEventListener("click", () => showQuestion(current - 1));

        element.querySelector("[data-test-next]").addEventListener("click", function () {
            if (current === questionNodes.length - 1) {
                finishTest();
            } else {
                showQuestion(current + 1);
            }
        });

        element.querySelector("[data-test-finish]").addEventListener("click", finishTest);

        setMode(mode);
        updateProgress();
    }

    element.querySelector("[data-test-restart]").addEventListener("click", function () {
        beginTest();
        window.scrollTo({ top: 0, behavior: "smooth" });
    });

    // --- Запуск -------------------------------------------------------------

    element.querySelector("[data-test-gate-open]").addEventListener("click", openGate);

    gate.addEventListener("click", function (event) {
        if (event.target === gate || event.target.closest("[data-test-gate-close]")) {
            closeGate();
        }
    });

    document.addEventListener("keydown", function (event) {
        if (event.key === "Escape" && gateOpen) {
            closeGate();
        }
    });

    if (!verified) {
        openGate();
    }
}
